import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { ChevronDownIcon, SearchIcon } from './icons';
import { Input } from './input';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './menu';

export const FilterBar = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn('flex items-center gap-2 border-b border-border-sunken px-page py-3', className)}
  >
    {children}
  </div>
);

/** One filter's button in the bar: a menu trigger or an on/off toggle. */
export const FilterChip = ({
  on,
  className,
  ...rest
}: ComponentProps<'button'> & { on?: boolean }) => (
  <button
    type="button"
    className={cn(
      'inline-flex h-control-sm items-center gap-2 rounded-control bg-chip px-flat text-meta text-muted',
      on && 'bg-chip-on text-primary',
      className,
    )}
    {...rest}
  />
);

interface IFilterMenuProps<TValue extends string> {
  label: string;
  value: TValue | undefined;
  options: readonly { value: TValue; label: string }[];
  onChange: (value: TValue | undefined) => void;
}

/** "Label: any" with a menu of the values; "Any" clears it. */
export const FilterMenu = <TValue extends string>({
  label,
  value,
  options,
  onChange,
}: IFilterMenuProps<TValue>) => (
  <Menu>
    <MenuTrigger asChild>
      <FilterChip>
        {label}: {options.find((option) => option.value === value)?.label ?? 'any'}
        <ChevronDownIcon size={11} />
      </FilterChip>
    </MenuTrigger>
    <MenuContent align="start">
      <MenuItem selected={value === undefined} onSelect={() => onChange(undefined)}>
        Any
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option.value}
          selected={value === option.value}
          onSelect={() => onChange(option.value)}
        >
          {option.label}
        </MenuItem>
      ))}
    </MenuContent>
  </Menu>
);

/** The list's search, at the bar's right end. */
export const FilterSearch = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) => (
  <Input
    size="sm"
    type="search"
    aria-label={placeholder}
    placeholder={placeholder}
    leading={<SearchIcon size={13} />}
    className="ml-auto w-search"
    value={value ?? ''}
    onChange={(event) => onChange(event.target.value || undefined)}
  />
);
